import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert as MuiAlert,
} from '@mui/material';

import { dashboardService, Fund, RegulatoryUpdate, FundNetworkData } from '../services/dashboardService';
import DashboardCharts from '../components/charts/DashboardCharts';
import { sseService } from '../services/websocketService';
import { useAuthStore } from '../store/authStore';
import { LoggerService } from '../services/LoggerService';

const DashboardPage: React.FC = () => {
  // State for data
  const [topFunds, setTopFunds] = useState<Fund[]>([]);
  const [regulatoryUpdates, setRegulatoryUpdates] = useState<RegulatoryUpdate[]>([]);
  const [networkData, setNetworkData] = useState<FundNetworkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sampleDataCreated, setSampleDataCreated] = useState(false);

  const { user } = useAuthStore();

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Setup SSE connection for real-time updates
  useEffect(() => {
    if (user?.id) {
      sseService.connect(user.id);

      // Listen for various event types
      sseService.on('alert', (data) => {
        LoggerService.debug('[Dashboard] New alert received:', data);
        loadDashboardData(); // Refresh dashboard
      });

      sseService.on('regulatory_update', (data) => {
        LoggerService.debug('[Dashboard] Regulatory update received:', data);
        loadDashboardData();
      });

      sseService.on('cash_position_update', (data) => {
        LoggerService.debug('[Dashboard] Cash position update received:', data);
        loadDashboardData();
      });

      sseService.on('position_break', (data) => {
        LoggerService.debug('[Dashboard] Position break detected:', data);
        loadDashboardData();
      });

      return () => {
        sseService.disconnect();
      };
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [fundsData, updatesData, networkData] = await Promise.all([
        dashboardService.getTopFunds(10),
        dashboardService.getRegulatoryUpdates(10),
        dashboardService.getFundNetworkData()
      ]);
      
      setTopFunds(fundsData);
      setRegulatoryUpdates(updatesData);
      setNetworkData(networkData);
      
      // Check if any data was loaded to determine if sample data exists
      if (fundsData.length > 0 || updatesData.length > 0) {
        setSampleDataCreated(true);
      } else {
        setSampleDataCreated(false);
      }

    } catch (err: any) {
      LoggerService.error('Error loading dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const createSampleData = async () => {
    try {
      await dashboardService.createSampleData();
      setSampleDataCreated(true);
      await loadDashboardData(); // Reload data after creating sample data
    } catch (err: any) {
      LoggerService.error('Error creating sample data:', err);
      setError(err.response?.data?.detail || 'Failed to create sample data');
    }
  };

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <MuiAlert severity="error" sx={{ mb: 3 }}>
          {error}
        </MuiAlert>
      )}

      {/* Sample Data Creation */}
      {!sampleDataCreated && !loading && (
        <MuiAlert 
          severity="info" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={createSampleData}>
              Create Sample Data
            </Button>
          }
        >
          No data available. Click to create sample data for testing.
        </MuiAlert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      )}

             {/* Dashboard Content */}
             {!loading && (
               <>
                 {/* Page Header */}
                 <Box mb={4}>
                   <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                     Financial Intelligence Dashboard
                   </Typography>
                   <Typography variant="body1" color="text.secondary">
                     Advanced analytics, regulatory monitoring, and fund structure analysis
                   </Typography>
                 </Box>

                 {/* Analytics & Visualizations */}
                 {networkData && (
                   <DashboardCharts
                     funds={topFunds}
                     regulatoryUpdates={regulatoryUpdates}
                     networkData={networkData}
                     onRefresh={loadDashboardData}
                   />
                 )}
               </>
             )}
    </Box>
  );
};

export default DashboardPage;
